import { createSelector, type OutputSelector } from 'reselect';
import { isWithinInterval, subDays, startOfDay, endOfDay, parseISO } from 'date-fns';
import type { ContextState } from '@jira/issue__context-state/src/ContextState.types.tsx';
import type { State } from '@jira/issue__redux-state-types/src/ReduxState.types.tsx';
import type { Locale } from '@jira/platform__supported-locales/src/supported-locales.tsx';
import {
	type ApplicationLicenseStates,
	LICENSE_STATE_ACTIVE,
} from '@jira/platform__license-state/src/license-state.tsx';
import type { ApplicationEditions } from '@jira/platform__application-editions/src/edition.tsx';
import type {
	ApplicationPermissions,
	LicensedProducts,
	ProductCrossSellConfig,
	SiteAdminStatus,
} from '@jira/platform__tenant-context-types/src/tenant-context.tsx';
import { extractProjectKey } from '@jira/platform__issue-key-parsing/src/extractProjectKey.tsx';
import {
	serviceDeskConfiguration,
	simpleSoftwareBoardConfiguration,
	softwareConfiguration,
	standardRoadmapConfiguration,
} from '@jira/issue__issue-view-configurations/src/ecosystemCustomFields.tsx';
import type { EntitiesState } from '@jira/issue__redux-state-types/src/EntitiesState.types.tsx';
import type { MediaContext } from '@jira/issue__redux-state-types/src/MediaContext.types.tsx';
import type { UiState } from '@jira/issue__redux-state-types/src/UiState.types.tsx';
import type { FieldsState } from '@jira/issue__shared-types/src/TypesServerField.tsx';
import type { DocNode } from '@atlaskit/adf-schema/doc';
import {
	type AnalyticsSource,
	BITBUCKET_ISSUE,
	MOBILE_ISSUE,
} from '@jira/platform__common-constants/src/analytics-sources.tsx';
import type { ApplicationKey } from '@jira/platform__common-constants/src/application-keys.tsx';
import { LICENSED_PRODUCTS } from '@atlassian/jira-common-util-get-tenant-context/src/index.tsx';
import { createAri, type Ari } from '@jira/platform__utils-ari/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { createSelectorWithLogging } from './createSelectorWithLogging.tsx';

type ActivationId = string;

type AccountId = string;
type CloudId = string;
type IssueId = string;
type IssueKey = string;
type ProjectKey = string;

type ViewModeType = 'MODAL' | 'SIDEBAR' | 'DEFAULT';

export const contextSelector = (state: State): ContextState => state.context;

export const accountIdloggedInUserSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	string | null,
	(res: ContextState) => string | null
> = createSelector(
	contextSelector,
	(context: ContextState): AccountId | null => context.atlassianAccountId,
);

export const orgIdSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	string | null,
	(res: ContextState) => string | null
> = createSelector(
	contextSelector,
	(context: ContextState): string | null => context?.orgId || null,
);

/**
 * @deprecated - use useLoginRedirectUrl instead
 */
export const loginRedirectUrlSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	string | undefined,
	(res: ContextState) => string | undefined
> = createSelector(
	contextSelector,
	(context: ContextState): string | undefined => context.loginRedirectUrl,
);

/**
 * @deprecated - use useIssueKey instead
 */
export const issueKeySelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	string,
	(res: ContextState) => string
> = createSelector(contextSelector, (context: ContextState): IssueKey => context.issueKey);

/**
 * @deprecated - use useIssueId instead
 */
export const issueIdSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	string | undefined,
	(res: ContextState) => string | undefined
> = createSelector(
	contextSelector,
	(context: ContextState): IssueId | undefined => context.issueId,
);

export const projectKeySelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	string,
	(res: string) => string
> = createSelector(
	issueKeySelector,
	(issueKey: IssueKey): ProjectKey => extractProjectKey(issueKey),
);

/**
 *  @deprecated - use useAnalyticsSource instead.
 */
export const analyticsSourceSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	string | undefined,
	(res: ContextState) => string | undefined
> = createSelector(contextSelector, (context: ContextState) =>
	context.analyticsSource !== undefined ? context.analyticsSource : undefined,
);

export const cloudIdSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	string,
	(res: ContextState) => string
> = createSelector(contextSelector, (context: ContextState): CloudId => context.cloudId || '');

export const issueAriSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	string,
	(res1: string, res2: string | undefined) => string
> = createSelector(
	cloudIdSelector,
	issueIdSelector,
	(cloudId: CloudId, issueId: IssueId | undefined): Ari =>
		createAri({
			resourceOwner: 'jira',
			cloudId,
			resourceType: 'issue',
			resourceId: issueId ?? '',
		}),
);

export const commentAriSelector = (
	commentId: string,
): OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	string,
	(res1: string, res2: string | undefined) => string
> =>
	createSelector(
		cloudIdSelector,
		issueIdSelector,
		(cloudId: CloudId, issueId: IssueId | undefined): Ari =>
			`ari:cloud:jira:${cloudId}:issue-comment/${issueId ?? ''}/${commentId}`,
	);

export const activationIdSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	string,
	(res: ContextState) => string
> = createSelector(
	contextSelector,
	(context: ContextState): ActivationId => context.activationId || '',
);

export const isAnonymousSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	boolean,
	(res: ContextState) => boolean
> = createSelector(contextSelector, (context: ContextState): boolean => context.isAnonymous);

const localeSelectorWithLogging = createSelectorWithLogging(
	contextSelector,
	(context: ContextState): Locale => context.locale,
);

const localeSelectorWithoutLogging = createSelector(
	contextSelector,
	(context: ContextState): Locale => context.locale,
);

export const localeSelector = (state: State): Locale => {
	// fg value not available at the module level :-https://hello.atlassian.net/wiki/spaces/JFP/blog/2025/06/02/4786217813/Write+your+frontend+for+SSR+Request-specific+code#Final-(bonus)-step---Lift-your-imports
	return fg('jiv-21686-adding-logging-for-selectors-error')
		? localeSelectorWithLogging(state)
		: localeSelectorWithoutLogging(state);
};

export const issueActionsSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	string[],
	(res: ContextState) => string[]
> = createSelector(
	contextSelector,
	(context: ContextState): string[] => context.issueActions || [],
);

/**
 * @deprecated `applicationKey` is deprecated because it is unreliable and is causing bugs from time to time
 */
export const applicationKeySelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	ApplicationKey | undefined,
	(res: ContextState) => ApplicationKey | undefined
> = createSelector(
	contextSelector,
	(context: ContextState): ApplicationKey | undefined => context.applicationKey,
);

export const isMobileSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	boolean,
	(res: AnalyticsSource | undefined) => boolean
> = createSelector(
	// @ts-expect-error - TS2769 - No overload matches this call.
	analyticsSourceSelector,
	(analyticsSource?: AnalyticsSource): boolean => analyticsSource === MOBILE_ISSUE,
);

/**
 * @deprecated `applicationKey` is deprecated because it is unreliable and is causing bugs from time to time
 * Replace usages with isSimplifiedProjectSelector()
 */
export const isAgilityProjectSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	boolean,
	(res: ApplicationKey | undefined) => boolean
> = createSelector(
	applicationKeySelector,
	(applicationKey?: ApplicationKey): boolean =>
		applicationKey === simpleSoftwareBoardConfiguration().applicationKey ||
		applicationKey === standardRoadmapConfiguration().applicationKey,
);

/**
 * @deprecated because`applicationKey` is deprecated because it is unreliable and is causing bugs from time to time
 * Use projectTypeSelector() instead
 */
export const isSoftwareSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	boolean,
	(res: ApplicationKey | undefined) => boolean
> = createSelector(
	applicationKeySelector,
	(applicationKey?: ApplicationKey): boolean =>
		applicationKey === softwareConfiguration().applicationKey,
);

export const isBitbucketSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	boolean,
	(res: AnalyticsSource | undefined) => boolean
> = createSelector(
	// @ts-expect-error - TS2769 - No overload matches this call.
	analyticsSourceSelector,
	(analyticsSource?: AnalyticsSource): boolean => analyticsSource === BITBUCKET_ISSUE,
);

/**
 * @deprecated because `applicationKey` is deprecated because it is unreliable and is causing bugs from time to time
 * Use projectTypeSelector() instead
 */
export const isServiceDeskSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	boolean,
	(res: ApplicationKey | undefined) => boolean
> = createSelector(
	applicationKeySelector,
	(applicationKey?: ApplicationKey): boolean =>
		applicationKey === serviceDeskConfiguration().applicationKey,
);

export const isEcosystemEnabledSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	boolean,
	(res: ContextState) => boolean
> = createSelector(contextSelector, (context: ContextState): boolean => !!context.ecosystemEnabled);

/**
 * @deprecated use useRapidViewId instead
 */
export const rapidViewIdSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	number | undefined,
	(res: ContextState) => number | undefined
> = createSelector(
	contextSelector,
	(context: ContextState): number | undefined => context.rapidViewId,
);

/**
 * @deprecated use useEstimationStatistic instead
 */
export const estimationStatisticSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	string | null,
	(res: ContextState) => string | null
> = createSelector(
	contextSelector,
	(context: ContextState): string | null => context.estimationStatistic || null,
);

/**
 * @deprecated use useBoardType instead
 */
export const boardTypeSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	string | null | undefined,
	(res: ContextState) => string | null | undefined
> = createSelector(
	contextSelector,
	(context: ContextState): string | null | undefined => context.boardType,
);

/**
 * @deprecated use useBoardId instead
 */
export const boardIdSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	number | null | undefined,
	(res: ContextState) => number | null | undefined
> = createSelector(
	contextSelector,
	(context: ContextState): number | null | undefined => context.boardId,
);

export const siteAdminStatusSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	SiteAdminStatus,
	(res: ContextState) => SiteAdminStatus
> = createSelector(
	contextSelector,
	(context: ContextState): SiteAdminStatus => context.siteAdminStatus,
);

export const productCrossSellConfigSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	ProductCrossSellConfig,
	(res: ContextState) => ProductCrossSellConfig
> = createSelector(
	contextSelector,
	(context: ContextState): ProductCrossSellConfig => context.productCrossSellConfig,
);

export const licensedProductsSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	LicensedProducts,
	(res: ContextState) => LicensedProducts
> = createSelector(
	contextSelector,
	(context: ContextState): LicensedProducts => context.licensedProducts,
);

export const appPermissionsSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	ApplicationPermissions,
	(res: ContextState) => ApplicationPermissions
> = createSelector(
	contextSelector,
	(context: ContextState): ApplicationPermissions => context.appPermissions,
);

/**
 * @deprecated: - use useIsModal instead
 */
export const isModalSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	boolean,
	(res: ContextState) => boolean
> = createSelector(contextSelector, (context: ContextState): boolean => context.isModal);

export const shouldRenderHeaderSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	boolean,
	(res1: boolean, res2: boolean) => boolean
> = createSelector(
	isModalSelector,
	isMobileSelector,
	(isModal, isMobile): boolean => !isModal && !isMobile,
);

/**
 * @deprecated: - use useViewMode instead
 */
export const viewModeSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	ViewModeType | null,
	(res: ContextState) => ViewModeType | null
> = createSelector(
	contextSelector,
	(context: ContextState): ViewModeType | null => context.viewMode,
);

export const appEditionsSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	ApplicationEditions,
	(res: ContextState) => ApplicationEditions
> = createSelector(
	contextSelector,
	(context: ContextState): ApplicationEditions => context.appEditions,
);

export const licenseStatesSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	{
		readonly core: 'ACTIVE' | 'SUSPENDED' | 'EXPIRED' | 'UNLICENSED';
		readonly software: 'ACTIVE' | 'SUSPENDED' | 'EXPIRED' | 'UNLICENSED';
		readonly serviceDesk: 'ACTIVE' | 'SUSPENDED' | 'EXPIRED' | 'UNLICENSED';
		readonly customerService: 'ACTIVE' | 'SUSPENDED' | 'EXPIRED' | 'UNLICENSED';
		readonly productDiscovery: 'ACTIVE' | 'SUSPENDED' | 'EXPIRED' | 'UNLICENSED';
	},
	(res: ContextState) => {
		readonly core: 'ACTIVE' | 'SUSPENDED' | 'EXPIRED' | 'UNLICENSED';
		readonly software: 'ACTIVE' | 'SUSPENDED' | 'EXPIRED' | 'UNLICENSED';
		readonly serviceDesk: 'ACTIVE' | 'SUSPENDED' | 'EXPIRED' | 'UNLICENSED';
		readonly customerService: 'ACTIVE' | 'SUSPENDED' | 'EXPIRED' | 'UNLICENSED';
		readonly productDiscovery: 'ACTIVE' | 'SUSPENDED' | 'EXPIRED' | 'UNLICENSED';
	}
> = createSelector(contextSelector, (context: ContextState) => context.licenseStates);

export const isServiceDeskLicenseActiveSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	boolean,
	(res: {
		readonly core: 'ACTIVE' | 'SUSPENDED' | 'EXPIRED' | 'UNLICENSED';
		readonly software: 'ACTIVE' | 'SUSPENDED' | 'EXPIRED' | 'UNLICENSED';
		readonly serviceDesk: 'ACTIVE' | 'SUSPENDED' | 'EXPIRED' | 'UNLICENSED';
		readonly customerService: 'ACTIVE' | 'SUSPENDED' | 'EXPIRED' | 'UNLICENSED';
		readonly productDiscovery: 'ACTIVE' | 'SUSPENDED' | 'EXPIRED' | 'UNLICENSED';
	}) => boolean
> = createSelector(
	licenseStatesSelector,
	(licenseStates: ApplicationLicenseStates) =>
		licenseStates && licenseStates.serviceDesk === LICENSE_STATE_ACTIVE,
);

export const tenantHasActiveServiceDeskLicenseSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	boolean,
	(res1: LicensedProducts, res2: boolean) => boolean
> = createSelector(
	licensedProductsSelector,
	isServiceDeskLicenseActiveSelector,
	(licensedProducts, isServiceDeskLicenseActive) =>
		licensedProducts &&
		licensedProducts[LICENSED_PRODUCTS.JIRA_SERVICEDESK] &&
		isServiceDeskLicenseActive,
);

export const isProductDiscoveryLicenseActiveSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	boolean,
	(res: {
		readonly core: 'ACTIVE' | 'SUSPENDED' | 'EXPIRED' | 'UNLICENSED';
		readonly software: 'ACTIVE' | 'SUSPENDED' | 'EXPIRED' | 'UNLICENSED';
		readonly serviceDesk: 'ACTIVE' | 'SUSPENDED' | 'EXPIRED' | 'UNLICENSED';
		readonly customerService: 'ACTIVE' | 'SUSPENDED' | 'EXPIRED' | 'UNLICENSED';
		readonly productDiscovery: 'ACTIVE' | 'SUSPENDED' | 'EXPIRED' | 'UNLICENSED';
	}) => boolean
> = createSelector(
	licenseStatesSelector,
	(licenseStates: ApplicationLicenseStates) =>
		licenseStates && licenseStates.productDiscovery === LICENSE_STATE_ACTIVE,
);

export const tenantHasActiveProductDiscoveryLicenseSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	boolean,
	(res1: LicensedProducts, res2: boolean) => boolean
> = createSelector(
	licensedProductsSelector,
	isProductDiscoveryLicenseActiveSelector,
	(licensedProducts, isProductDiscoveryLicenseActive) =>
		Boolean(
			licensedProducts &&
			licensedProducts[LICENSED_PRODUCTS.JIRA_PRODUCT_DISCOVERY] &&
			isProductDiscoveryLicenseActive,
		),
);

export const confluenceActivationDateTraitSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	string | undefined,
	(res: ContextState) => string | undefined
> = createSelector(
	contextSelector,
	(context: ContextState): string | undefined => context.confluenceActivationDate,
);

export const hasActivatedConfluenceWithinFourteenDaysSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	boolean,
	(res: string | undefined) => boolean
> = createSelector(confluenceActivationDateTraitSelector, (confluenceActivationDate) => {
	try {
		if (!confluenceActivationDate) return false;
		const today = endOfDay(new Date());
		const fourteenDaysAgo = startOfDay(subDays(today, 14));
		const dateToCheck = parseISO(confluenceActivationDate);

		return isWithinInterval(dateToCheck, {
			start: fourteenDaysAgo,
			end: today,
		});
	} catch (error) {
		return false;
	}
});

export const hasActivatedConfluenceWithinThirtyDaysSelector: OutputSelector<
	Readonly<{
		agile: {
			epic?: {
				key: string;
			};
		};
		context: ContextState;
		entities: EntitiesState;
		fields: FieldsState;
		mediaContext: MediaContext;
		shareAsComment: {
			timestamp: number;
			value?: DocNode;
		};
		timing: {
			server: {
				[key: string]: number;
			};
		};
		ui: UiState;
		validators: {
			projectHasSubtaskValidators:
				| {
						status: 'LOADING';
				  }
				| {
						status: 'FAILED';
				  }
				| {
						status: 'LOADED';
						value: boolean;
				  };
		};
	}>,
	boolean,
	(res: string | undefined) => boolean
> = createSelector(confluenceActivationDateTraitSelector, (confluenceActivationDate) => {
	try {
		if (!confluenceActivationDate) return false;
		const today = endOfDay(new Date());
		const thirtyDaysAgo = startOfDay(subDays(today, 31)); // 31 days to account for the 30 day trial
		const dateToCheck = parseISO(confluenceActivationDate);

		return isWithinInterval(dateToCheck, {
			start: thirtyDaysAgo,
			end: today,
		});
	} catch (error) {
		return false;
	}
});
